/**
 * Actions cell for a tenant/booking row in the Bookings & Tenants list.
 *
 * Combines the tenant-lifecycle actions (Approve / Reassign / Act as
 * tenant / Archive) with the account-management actions that live on the
 * Settings → user directory (Send login link / Set password / Reset password /
 * Deactivate / Delete) — the latter reuse the same admin edge functions.
 *
 * Rendered as a standalone component (not inline in the row-render helper) so it
 * can own the confirm/password dialog state: the dropdown closing on select
 * would otherwise unmount an inline dialog before it opened. Account actions are
 * hidden for guest bookings (no auth account / no user_id).
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  CheckCircle,
  KeyRound,
  Link2,
  LogIn,
  MoreHorizontal,
  Replace,
  RotateCcw,
  Trash2,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { useLanguage } from "@/contexts/LanguageContext";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

export interface TenantRowActionsProps {
  tenant: any;
  isSuperAdmin: boolean;
  /**
   * Admin or super_admin. Gates the non-destructive account actions (send login
   * link, set/reset password) whose edge functions accept `admin` too, and
   * Approve — a booking-status write the DB trigger refuses from anyone else.
   * The destructive ones (deactivate/delete) stay super-admin-only via
   * isSuperAdmin.
   */
  canManageAccount: boolean;
  /**
   * Admin or super_admin. Gates "Act as tenant" — the edge function accepts
   * both, so this is deliberately wider than isSuperAdmin.
   */
  canActAs: boolean;
  currentUserId: string | null;
  onApprove: (bookingId: number) => void;
  approving: boolean;
  onReassign: (payload: {
    bookingId: number;
    tenantName: string;
    fromRoomId: number;
    propertyId: number;
  }) => void;
  onActAsTenant: (userId: string) => void;
  onArchive: (payload: { id: number; archive: boolean }) => void;
  archiving: boolean;
  /**
   * Overrides the "…" trigger's styling, so the menu can sit among other
   * header controls (the tenant profile) and not only in a table cell.
   */
  triggerClassName?: string;
  /** Fires after the user's account was deleted (e.g. to close a profile). */
  onDeleted?: () => void;
}

export function TenantRowActions({
  tenant,
  isSuperAdmin,
  canManageAccount,
  canActAs,
  currentUserId,
  onApprove,
  approving,
  onReassign,
  onActAsTenant,
  onArchive,
  archiving,
  triggerClassName,
  onDeleted,
}: TenantRowActionsProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  // Refetch the caches that render account state so a deactivate/delete drops
  // the stale row instead of lingering until something else refetches. Mirrors
  // the invalidation AdminRolePanels runs on the same account actions.
  const invalidateAccountViews = () => {
    void invalidateOccupancyViews(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
    // AdminTenants joins profile.is_active from this query, not the bookings
    // one — without it the Deactivate/Activate item stays stale after a toggle.
    void queryClient.invalidateQueries({ queryKey: ["admin-all-profiles"] });
  };

  const userId: string | null = tenant.userId ?? null;
  const email: string =
    tenant.profile?.email || tenant.guestEmail || tenant.email || "";
  const name: string =
    tenant.profile?.name || tenant.guestName || tenant.name || "";
  const isActive: boolean = tenant.profile?.is_active !== false;
  const isSelf = !!userId && userId === currentUserId;
  // Account actions require a real auth account. Login-link/password actions are
  // available to admin or super_admin (canManageAccount); the destructive ones
  // (deactivate/delete) stay super-admin-only below. Guests (no user_id) and the
  // caller's own row never get them.
  const showAccountActions = !!userId && canManageAccount && !isSelf;

  const [pwOpen, setPwOpen] = useState(false);
  const [pwMode, setPwMode] = useState<"set" | "reset">("set");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const sendLoginLink = async () => {
    if (!email) {
      toast.error(
        t("admin.tenantRowActions.noEmail", "No email on file for this tenant.")
      );
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        message?: string;
      }>("admin-send-login-link", {
        body: { email, role: "tenant", name, app_url: window.location.origin },
      });
      if (error) {
        toast.error(
          await edgeFunctionErrorMessage(
            error,
            t(
              "admin.tenantRowActions.loginLinkFailed",
              "Failed to send login link."
            )
          )
        );
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(
        data?.message ??
          `${t("admin.tenantRowActions.loginLinkSentPrefix", "Login link sent to")} ${email}.`
      );
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.tenantRowActions.loginLinkFailed",
              "Failed to send login link."
            )
      );
    }
  };

  const openPw = (mode: "set" | "reset") => {
    setPwMode(mode);
    setPwNew("");
    setPwConfirm("");
    setPwOpen(true);
  };

  const submitPassword = async () => {
    if (!userId) return;
    if (pwNew.length < 6) {
      toast.error(
        t(
          "admin.tenantRowActions.pwTooShort",
          "Password must be at least 6 characters."
        )
      );
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error(
        t("admin.tenantRowActions.pwMismatch", "Passwords do not match.")
      );
      return;
    }
    setPwBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        message?: string;
      }>("admin-set-user-password", {
        body: { user_id: userId, password: pwNew },
      });
      if (error) {
        toast.error(
          error.message ||
            t("admin.tenantRowActions.pwSetFailed", "Failed to set password.")
        );
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(
        data?.message ??
          t("admin.tenantRowActions.pwUpdated", "Password updated.")
      );
      setPwOpen(false);
    } finally {
      setPwBusy(false);
    }
  };

  const confirmToggleActive = async () => {
    if (!userId) return;
    setToggling(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        message?: string;
      }>("admin-deactivate-user", {
        body: { user_id: userId, is_active: !isActive },
      });
      if (error) {
        toast.error(
          error.message ||
            t(
              "admin.tenantRowActions.statusUpdateFailed",
              "Failed to update account status."
            )
        );
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(
        isActive
          ? `${email || t("admin.tenantRowActions.account", "Account")} ${t("admin.tenantRowActions.hasBeenDeactivated", "has been deactivated.")}`
          : `${email || t("admin.tenantRowActions.account", "Account")} ${t("admin.tenantRowActions.hasBeenActivated", "has been activated.")}`
      );
      invalidateAccountViews();
      setDeactivateOpen(false);
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.tenantRowActions.statusUpdateFailed",
              "Failed to update account status."
            )
      );
    } finally {
      setToggling(false);
    }
  };

  const confirmDelete = async () => {
    if (!userId) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        message?: string;
      }>("admin-delete-user", {
        body: { user_id: userId },
      });
      if (error) {
        // Non-2xx: the real JSON body ({ error }) is on error.context (e.g. the
        // "user still owns bookings" guard) — surface it, not the generic text.
        let message = t(
          "admin.tenantRowActions.deleteFailed",
          "Failed to delete user."
        );
        const ctx = (
          error as { context?: { json?: () => Promise<{ error?: string }> } }
        ).context;
        if (ctx && typeof ctx.json === "function") {
          try {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          } catch {
            /* fall through */
          }
        } else if (error.message) {
          message = error.message;
        }
        toast.error(message);
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(
        `${email || t("admin.tenantRowActions.user", "User")} ${t("admin.tenantRowActions.hasBeenDeleted", "has been deleted.")}`
      );
      invalidateAccountViews();
      setDeleteOpen(false);
      onDeleted?.();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.tenantRowActions.deleteFailed", "Failed to delete user.")
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("admin.tenantRowActions.moreActions", "More actions")}
            className={
              triggerClassName ?? "h-9 w-9 rounded-full hover:bg-muted/50"
            }
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {/* Tenant-lifecycle actions */}
          {canManageAccount &&
            tenant.status === "pending" &&
            !tenant.adminArchivedAt && (
              <DropdownMenuItem
                onClick={() => onApprove(tenant.id)}
                disabled={approving}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {approving
                  ? t("admin.tenantRowActions.approving", "Approving...")
                  : t("admin.tenantRowActions.approve", "Approve")}
              </DropdownMenuItem>
            )}
          {isSuperAdmin &&
            !tenant.adminArchivedAt &&
            tenant.room?.id &&
            tenant.property?.id && (
              <DropdownMenuItem
                onClick={() =>
                  onReassign({
                    bookingId: tenant.id,
                    tenantName:
                      name ||
                      `${t("admin.tenantRowActions.tenantHashPrefix", "Tenant #")}${tenant.id}`,
                    fromRoomId: tenant.room.id,
                    propertyId: tenant.property.id,
                  })
                }
              >
                <Replace className="mr-2 h-4 w-4" />
                {t("admin.tenantRowActions.reassignRoom", "Reassign room…")}
              </DropdownMenuItem>
            )}
          {canActAs && userId && (
            <DropdownMenuItem onClick={() => onActAsTenant(userId)}>
              <LogIn className="mr-2 h-4 w-4" />
              {t("admin.tenantRowActions.actAsTenant", "Act as tenant")}
            </DropdownMenuItem>
          )}
          {tenant.adminArchivedAt ? (
            <DropdownMenuItem
              onClick={() => onArchive({ id: tenant.id, archive: false })}
              disabled={archiving}
            >
              <ArchiveRestore className="mr-2 h-4 w-4" />
              {t("admin.tenantRowActions.unarchive", "Unarchive")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => onArchive({ id: tenant.id, archive: true })}
              disabled={archiving}
              className="text-muted-foreground"
            >
              <Archive className="mr-2 h-4 w-4" />
              {t("admin.tenantRowActions.archive", "Archive")}
            </DropdownMenuItem>
          )}

          {/* Account-management actions (from Settings). Account-holders only. */}
          {showAccountActions && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("admin.tenantRowActions.accountLabel", "Account")}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => void sendLoginLink()}>
                <Link2 className="mr-2 h-4 w-4" />
                {t("admin.tenantRowActions.sendLoginLink", "Send login link")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openPw("set")}>
                <KeyRound className="mr-2 h-4 w-4" />
                {t("admin.tenantRowActions.setPassword", "Set password")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openPw("reset")}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("admin.tenantRowActions.resetPassword", "Reset password")}
              </DropdownMenuItem>
              {/* Destructive account actions — super-admin only (their edge
                  functions reject non-super callers). */}
              {isSuperAdmin && (
                <>
                  <DropdownMenuItem
                    onClick={() => setDeactivateOpen(true)}
                    className="text-amber-600"
                  >
                    <UserX className="mr-2 h-4 w-4" />
                    {isActive
                      ? t(
                          "admin.tenantRowActions.deactivateAccount",
                          "Deactivate account"
                        )
                      : t(
                          "admin.tenantRowActions.activateAccount",
                          "Activate account"
                        )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeleteOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("admin.tenantRowActions.deleteUser", "Delete user")}
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Set / Reset password */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pwMode === "reset"
                ? t("admin.tenantRowActions.resetPassword", "Reset password")
                : t("admin.tenantRowActions.setPassword", "Set password")}
            </DialogTitle>
            <DialogDescription>
              {email
                ? `${t("admin.tenantRowActions.setNewPasswordForPrefix", "Set a new password for")} ${email}. ${t("admin.tenantRowActions.signInImmediately", "They can sign in with it immediately.")}`
                : t(
                    "admin.tenantRowActions.setNewPasswordGeneric",
                    "Set a new password for this account."
                  )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="tra-pw-new" className="text-xs font-medium">
                {t("admin.tenantRowActions.newPassword", "New password")}
              </Label>
              <Input
                id="tra-pw-new"
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder={t(
                  "admin.tenantRowActions.atLeast6Chars",
                  "At least 6 characters"
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tra-pw-confirm" className="text-xs font-medium">
                {t(
                  "admin.tenantRowActions.confirmPassword",
                  "Confirm password"
                )}
              </Label>
              <Input
                id="tra-pw-confirm"
                type="password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)}>
              {t("admin.tenantRowActions.cancel", "Cancel")}
            </Button>
            <Button onClick={() => void submitPassword()} disabled={pwBusy}>
              {pwBusy
                ? t("admin.tenantRowActions.saving", "Saving…")
                : t("admin.tenantRowActions.savePassword", "Save password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate / activate */}
      <AlertDialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isActive
                ? t(
                    "admin.tenantRowActions.deactivateAccountQ",
                    "Deactivate account?"
                  )
                : t(
                    "admin.tenantRowActions.activateAccountQ",
                    "Activate account?"
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isActive
                ? `${email || t("admin.tenantRowActions.thisTenant", "This tenant")} ${t("admin.tenantRowActions.blockedUntilReactivated", "will be blocked from signing in until reactivated.")}`
                : `${email || t("admin.tenantRowActions.thisTenant", "This tenant")} ${t("admin.tenantRowActions.willSignInAgain", "will be able to sign in again.")}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>
              {t("admin.tenantRowActions.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmToggleActive();
              }}
              disabled={toggling}
            >
              {toggling
                ? t("admin.tenantRowActions.working", "Working…")
                : isActive
                  ? t("admin.tenantRowActions.deactivate", "Deactivate")
                  : t("admin.tenantRowActions.activate", "Activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete user */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.tenantRowActions.deleteUserQ", "Delete user?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "admin.tenantRowActions.deleteUserConfirm",
                "This permanently deletes {subject}'s account. This cannot be undone."
              ).replace(
                "{subject}",
                email || t("admin.tenantRowActions.thisUser", "this user")
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("admin.tenantRowActions.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting
                ? t("admin.tenantRowActions.deleting", "Deleting…")
                : t("admin.tenantRowActions.deleteUser", "Delete user")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
